import type { ModelDefinition } from "@/models.js";

export const zaiModels = [
	{
		id: "glm-5.2",
		name: "GLM-5.2",
		description:
			"Zhipu GLM-5.2 flagship model for long-horizon coding and agentic engineering tasks with a 1M context window.",
		family: "zai",
		releasedAt: new Date("2026-06-13"),
		providers: [
			{
				providerId: "zai",
				externalId: "glm-5.2",
				inputPrice: "1.4e-6",
				cachedInputPrice: "0.26e-6",
				outputPrice: "4.4e-6",
				requestPrice: "0",
				contextSize: 1000000,
				maxOutput: 128000,
				streaming: true,
				reasoning: true,
				reasoningEfforts: ["none", "high", "max"],
				vision: false,
				tools: true,
				webSearch: true,
				webSearchPrice: "0.01",
				jsonOutput: true,
			},
			{
				providerId: "canopywave",
				test: "skip", // over-reasons heavily and streams slowly (~1 tok/s), so the 60s streaming timeout is flaky
				externalId: "zai/glm-5.2",
				inputPrice: "1.4e-6",
				cachedInputPrice: "0.26e-6",
				outputPrice: "4.4e-6",
				requestPrice: "0",
				contextSize: 200000,
				maxOutput: 32768,
				quantization: "fp8",
				streaming: true,
				reasoning: true,
				vision: false,
				tools: true,
				jsonOutput: true,
			},
			{
				providerId: "embercloud",
				externalId: "glm-5.2",
				// embercloud rate-limits this model on every request (429 even for
				// single spaced-out calls, verified 2026-07-14)
				stability: "unstable",
				inputPrice: "1.26e-6",
				outputPrice: "3.96e-6",
				cachedInputPrice: "0.234e-6",
				requestPrice: "0",
				contextSize: 203000,
				maxOutput: 131000,
				streaming: true,
				vision: false,
				tools: true,
				reasoning: true,
				jsonOutput: true,
				supportedParameters: [
					"messages",
					"model",
					"stream",
					"stream_options",
					"temperature",
					"top_p",
					"max_tokens",
					"max_completion_tokens",
					"seed",
					"stop",
					"response_format",
					"tools",
					"tool_choice",
					"parallel_tool_calls",
					"reasoning",
					"reasoning_effort",
				],
			},
			{
				providerId: "granite",
				externalId: "glm-5.2",
				deactivatedAt: new Date("2026-07-27"),
				inputPrice: "1.4e-6",
				cachedInputPrice: "0.26e-6",
				outputPrice: "4.4e-6",
				requestPrice: "0",
				contextSize: 1000000,
				maxOutput: 128000,
				streaming: true,
				reasoning: true,
				vision: false,
				tools: true,
				jsonOutput: true,
				supportsDeveloperRole: false,
			},
			{
				providerId: "bytedance",
				externalId: "glm-5-2-260617",
				inputPrice: "1.4e-6",
				cachedInputPrice: "0.26e-6",
				outputPrice: "4.4e-6",
				requestPrice: "0",
				contextSize: 1024000,
				maxOutput: 128000,
				streaming: true,
				reasoning: true,
				reasoningEfforts: [
					"none",
					"minimal",
					"low",
					"medium",
					"high",
					"xhigh",
					"max",
				],
				vision: false,
				tools: true,
				jsonOutput: true,
				healStreamingJsonOutput: true,
			},
			{
				providerId: "alibaba",
				externalId: "glm-5.2",
				inputPrice: "1.4e-6",
				cachedInputPrice: "0.28e-6",
				outputPrice: "4.4e-6",
				regions: [
					{ id: "singapore" },
					{ id: "eu-frankfurt" },
					{ id: "us-virginia" },
					{ id: "cn-beijing" },
				],
				requestPrice: "0",
				contextSize: 1000000,
				maxOutput: 131072,
				streaming: true,
				reasoning: true,
				reasoningEfforts: [
					"none",
					"minimal",
					"low",
					"medium",
					"high",
					"xhigh",
					"max",
				],
				reasoningMaxTokens: true,
				vision: false,
				tools: true,
				jsonOutput: true,
				supportsDeveloperRole: false,
			},
			{
				providerId: "runware",
				externalId: "zai-glm-5-2",
				inputPrice: "0.8e-6",
				outputPrice: "2.55e-6",
				cachedInputPrice: "0.16e-6",
				requestPrice: "0",
				contextSize: 1024000,
				maxOutput: 128000,
				quantization: "fp4",
				streaming: true,
				reasoning: true,
				vision: false,
				tools: true,
				jsonOutput: true,
			},
			{
				providerId: "nebius",
				externalId: "zai-org/GLM-5.2",
				inputPrice: "1.4e-6",
				outputPrice: "4.4e-6",
				requestPrice: "0",
				// The model card advertises 1,024K context, and Nebius accepted a
				// 52K-token prompt live (verified 2026-07-22).
				contextSize: 1048576,
				maxOutput: undefined,
				quantization: "fp4",
				streaming: true,
				reasoning: true,
				vision: false,
				tools: true,
				jsonOutput: true,
			},
			{
				providerId: "scx-ai-gp",
				externalId: "GLM-5.2",
				inputPrice: "0.55e-6",
				cachedInputPrice: "0.1375e-6",
				outputPrice: "1.9255e-6",
				requestPrice: "0",
				contextSize: 1000000,
				// SCX rejects max_tokens above 131072 ("expected a value <= 131072"),
				// despite its catalogue advertising a 128k ceiling
				maxOutput: 131072,
				quantization: "fp8",
				streaming: true,
				reasoning: true,
				vision: false,
				tools: true,
				jsonOutput: true,
			},
		],
	},
	{
		id: "glm-5.1",
		name: "GLM-5.1",
		description:
			"Zhipu GLM-5.1 flagship model engineered for long-horizon autonomous tasks with enhanced coding and agentic capabilities.",
		family: "zai",
		releasedAt: new Date("2026-04-07"),
		providers: [
			{
				providerId: "zai",
				externalId: "glm-5.1",
				inputPrice: "1.4e-6",
				cachedInputPrice: "0.26e-6",
				outputPrice: "4.4e-6",
				requestPrice: "0",
				contextSize: 200000,
				maxOutput: 128000,
				streaming: true,
				reasoning: true,
				vision: false,
				tools: true,
				webSearch: true,
				webSearchPrice: "0.01",
				jsonOutput: true,
			},
			{
				providerId: "novita",
				externalId: "zai-org/glm-5.1",
				inputPrice: "1.4e-6",
				cachedInputPrice: "0.26e-6",
				outputPrice: "4.4e-6",
				requestPrice: "0",
				contextSize: 204800,
				maxOutput: 131100,
				quantization: "fp8",
				streaming: true,
				reasoning: true,
				// novita's glm-5.1 reasons adaptively and omits reasoning_content
				// for simple prompts; no parameter forces it on
				reasoningOutput: "omit",
				vision: false,
				tools: true,
				jsonOutput: true,
				// novita disables thinking when reasoning_effort is forwarded
				// (empty reasoning_content); omitting it reasons by default, so
				// exclude reasoning_effort here (verified live 2026-07-14)
				supportedParameters: [
					"temperature",
					"max_tokens",
					"top_p",
					"frequency_penalty",
					"presence_penalty",
					"stop",
					"stream",
					"response_format",
					"tools",
					"tool_choice",
				],
			},
			{
				providerId: "together-ai",
				externalId: "zai-org/GLM-5.1",
				deactivatedAt: new Date("2026-07-10"),
				inputPrice: "1.4e-6",
				cachedInputPrice: "0.26e-6",
				outputPrice: "4.4e-6",
				requestPrice: "0",
				contextSize: 202752,
				maxOutput: 202752,
				streaming: true,
				reasoning: true,
				vision: false,
				tools: true,
				jsonOutput: true,
				jsonOutputSchema: true,
			},
			{
				providerId: "deepinfra",
				externalId: "zai-org/GLM-5.1",
				inputPrice: "1.05e-6",
				cachedInputPrice: "0.205e-6",
				outputPrice: "3.5e-6",
				requestPrice: "0",
				contextSize: 198000,
				maxOutput: 65536,
				quantization: "fp4",
				streaming: true,
				reasoning: true,
				vision: false,
				tools: true,
				jsonOutput: true,
			},
			{
				providerId: "embercloud",
				externalId: "glm-5.1",
				// embercloud returns 400 "Temporary routing error" on every request
				// for this model (verified 2026-07-14)
				stability: "unstable",
				inputPrice: "0.931e-6",
				outputPrice: "2.93e-6",
				cachedInputPrice: "0.173e-6",
				requestPrice: "0",
				contextSize: 203000,
				maxOutput: 131000,
				streaming: true,
				vision: false,
				tools: true,
				reasoning: true,
				jsonOutput: false,
				supportedParameters: [
					"messages",
					"model",
					"stream",
					"stream_options",
					"temperature",
					"top_p",
					"max_tokens",
					"max_completion_tokens",
					"seed",
					"stop",
					"response_format",
					"tools",
					"tool_choice",
					"parallel_tool_calls",
					"reasoning",
					"reasoning_effort",
				],
			},
			{
				providerId: "nebius",
				externalId: "zai-org/GLM-5.1",
				inputPrice: "1.4e-6",
				outputPrice: "4.4e-6",
				requestPrice: "0",
				contextSize: 202752,
				maxOutput: undefined,
				quantization: "fp8",
				streaming: true,
				reasoning: true,
				vision: false,
				tools: true,
				jsonOutput: true,
			},
		],
	},
	{
		id: "glm-5",
		name: "GLM-5",
		description: "Zhipu GLM-5 with advanced reasoning capabilities.",
		family: "zai",
		releasedAt: new Date("2026-02-15"),
		providers: [
			{
				providerId: "zai",
				externalId: "glm-5",
				inputPrice: "1e-6",
				cachedInputPrice: "0.2e-6",
				outputPrice: "3.2e-6",
				requestPrice: "0",
				contextSize: 202800,
				maxOutput: 131100,
				streaming: true,
				reasoning: true,
				vision: false,
				tools: true,
				webSearch: true,
				webSearchPrice: "0.01",
				jsonOutput: true,
			},
			{
				deactivatedAt: new Date("2026-04-22"),
				providerId: "together-ai",
				externalId: "zai-org/GLM-5",
				inputPrice: "1e-6",
				outputPrice: "3.2e-6",
				requestPrice: "0",
				contextSize: 202752,
				maxOutput: 202752,
				streaming: true,
				reasoning: true,
				vision: false,
				tools: true,
				jsonOutput: true,
				jsonOutputSchema: true,
			},
			{
				providerId: "novita",
				externalId: "zai-org/glm-5",
				inputPrice: "1e-6",
				cachedInputPrice: "0.2e-6",
				outputPrice: "3.2e-6",
				requestPrice: "0",
				contextSize: 202800,
				maxOutput: 131100,
				quantization: "fp8",
				streaming: true,
				reasoning: true,
				vision: false,
				tools: true,
				jsonOutput: true,
			},
			{
				test: "skip",
				providerId: "alibaba",
				externalId: "glm-5",
				inputPrice: "0.573e-6",
				outputPrice: "2.58e-6",
				pricingTiers: [
					{
						name: "Up to 32K",
						upToTokens: 32000,
						inputPrice: "0.573e-6",
						outputPrice: "2.58e-6",
					},
					{
						name: "Over 32K",
						upToTokens: Infinity,
						inputPrice: "0.86e-6",
						outputPrice: "3.154e-6",
					},
				],
				regions: [{ id: "cn-beijing" }],
				requestPrice: "0",
				contextSize: 202752,
				maxOutput: 16384,
				streaming: true,
				reasoning: true,
				reasoningMaxTokens: true,
				reasoningEfforts: ["none", "minimal", "low", "medium", "high", "xhigh"],
				vision: false,
				tools: true,
				jsonOutput: true,
			},
			{
				providerId: "embercloud",
				externalId: "glm-5",
				// embercloud reasoning requests time out repeatedly in e2e
				stability: "unstable",
				inputPrice: "0.72e-6",
				outputPrice: "2.3e-6",
				cachedInputPrice: "0.144e-6",
				requestPrice: "0",
				contextSize: 203000,
				maxOutput: 131000,
				streaming: true,
				vision: false,
				tools: true,
				reasoning: true,
				jsonOutput: false,
				supportedParameters: [
					"messages",
					"model",
					"stream",
					"stream_options",
					"temperature",
					"top_p",
					"max_tokens",
					"max_completion_tokens",
					"seed",
					"stop",
					"response_format",
					"tools",
					"tool_choice",
					"parallel_tool_calls",
					"reasoning",
					"reasoning_effort",
				],
			},
			{
				providerId: "nebius",
				externalId: "zai-org/GLM-5",
				deactivatedAt: new Date("2026-06-22"),
				inputPrice: "1.0e-6",
				outputPrice: "3.2e-6",
				requestPrice: "0",
				contextSize: 202752,
				maxOutput: 32768,
				streaming: true,
				reasoning: true,
				vision: false,
				tools: true,
				jsonOutput: true,
			},
			{
				providerId: "vertex-openai",
				externalId: "zai-org/glm-5-maas",
				deactivatedAt: new Date("2026-10-21"),
				inputPrice: "1e-6",
				cachedInputPrice: "0.1e-6",
				outputPrice: "3.2e-6",
				requestPrice: "0",
				contextSize: 202752,
				maxOutput: 32768,
				streaming: true,
				reasoning: true,
				vision: false,
				tools: true,
				jsonOutput: true,
			},
		],
	},
	{
		id: "glm-4.5",
		name: "GLM-4.5",
		description: "Zhipu GLM-4.5 with reasoning capabilities.",
		family: "zai",
		releasedAt: new Date("2025-07-28"),
		providers: [
			{
				providerId: "zai",
				externalId: "glm-4.5",
				// Tool calling is unreliable: zai returns tool calls as inline
				// <tool_call> XML in message content instead of structured
				// tool_calls, so the gateway cannot surface them.
				stability: "unstable",
				inputPrice: "0.6e-6",
				cachedInputPrice: "0.11e-6",
				outputPrice: "2.2e-6",
				requestPrice: "0",
				contextSize: 128000,
				maxOutput: undefined,
				streaming: true,
				reasoning: true,
				vision: false,
				tools: true,
				webSearch: true,
				webSearchPrice: "0.01",
				jsonOutput: true,
			},
			{
				providerId: "embercloud",
				externalId: "glm-4.5",
				// embercloud rate-limits this model on every request (429 even for
				// single spaced-out calls, verified 2026-07-14)
				stability: "unstable",
				inputPrice: "0.6e-6",
				outputPrice: "2.2e-6",
				cachedInputPrice: "0.11e-6",
				requestPrice: "0",
				contextSize: 131000,
				maxOutput: 96000,
				streaming: true,
				vision: false,
				tools: true,
				jsonOutput: true,
				// Embercloud bug: tool_choice other than "auto" breaks streaming tool calls for this model
				supportedParameters: [
					"messages",
					"model",
					"stream",
					"stream_options",
					"temperature",
					"top_p",
					"max_tokens",
					"max_completion_tokens",
					"seed",
					"stop",
					"response_format",
					"logprobs",
					"top_logprobs",
					"tools",
					"parallel_tool_calls",
				],
			},
		],
	},
	{
		id: "glm-4.5v",
		name: "GLM-4.5V",
		description: "GLM-4.5 with vision support.",
		family: "zai",
		releasedAt: new Date("2025-08-11"),
		providers: [
			{
				providerId: "zai",
				externalId: "glm-4.5v",
				inputPrice: "0.6e-6",
				cachedInputPrice: "0.11e-6",
				outputPrice: "1.8e-6",
				requestPrice: "0",
				contextSize: 128000,
				maxOutput: 16000,
				streaming: true,
				reasoning: true,
				reasoningOutput: "omit",
				vision: true,
				tools: true,
				jsonOutput: true,
			},
			{
				providerId: "novita",
				externalId: "zai-org/glm-4.5v",
				inputPrice: "0.6e-6",
				cachedInputPrice: "0.11e-6",
				outputPrice: "1.8e-6",
				requestPrice: "0",
				contextSize: 65536,
				maxOutput: 16000,
				quantization: "fp8",
				streaming: true,
				reasoning: true,
				reasoningOutput: "omit",
				vision: true,
				tools: true,
				jsonOutput: true,
			},
		],
	},
	{
		id: "glm-4.5-air",
		name: "GLM-4.5 Air",
		description: "Lightweight GLM-4.5 variant.",
		family: "zai",
		releasedAt: new Date("2025-07-25"),
		providers: [
			{
				providerId: "zai",
				externalId: "glm-4.5-air",
				inputPrice: "0.2e-6",
				cachedInputPrice: "0.03e-6",
				outputPrice: "1.1e-6",
				requestPrice: "0",
				contextSize: 128000,
				maxOutput: undefined,
				streaming: true,
				reasoning: false,
				vision: false,
				tools: true,
				stability: "unstable",
				jsonOutput: true,
			},
			{
				providerId: "embercloud",
				externalId: "glm-4.5-air",
				// embercloud rate-limits this model on every request (429 even for
				// single spaced-out calls, verified 2026-07-14)
				stability: "unstable",
				inputPrice: "0.13e-6",
				outputPrice: "0.85e-6",
				cachedInputPrice: "0.025e-6",
				requestPrice: "0",
				contextSize: 131000,
				maxOutput: 96000,
				streaming: true,
				vision: false,
				tools: true,
				jsonOutput: true,
				supportedParameters: [
					"messages",
					"model",
					"stream",
					"stream_options",
					"temperature",
					"top_p",
					"max_tokens",
					"max_completion_tokens",
					"seed",
					"stop",
					"response_format",
					"logprobs",
					"top_logprobs",
					"tools",
					"tool_choice",
					"parallel_tool_calls",
				],
			},
		],
	},
	{
		id: "glm-4.5-x",
		name: "GLM-4.5 X",
		description: "Extended GLM-4.5 with advanced reasoning.",
		family: "zai",
		releasedAt: new Date("2025-07-28"),
		providers: [
			{
				providerId: "zai",
				externalId: "glm-4.5-x",
				inputPrice: "2.2e-6",
				cachedInputPrice: "0.45e-6",
				outputPrice: "8.9e-6",
				requestPrice: "0",
				contextSize: 128000,
				maxOutput: undefined,
				streaming: true,
				reasoning: true,
				vision: false,
				tools: true,
				stability: "unstable", // unstable due to high limits
				jsonOutput: true,
			},
		],
	},
	{
		id: "glm-4.5-airx",
		name: "GLM-4.5 AirX",
		description: "Enhanced GLM-4.5 Air variant.",
		family: "zai",
		releasedAt: new Date("2025-07-28"),
		providers: [
			{
				providerId: "zai",
				externalId: "glm-4.5-airx",
				inputPrice: "1.1e-6",
				cachedInputPrice: "0.22e-6",
				outputPrice: "4.5e-6",
				requestPrice: "0",
				contextSize: 128000,
				maxOutput: undefined,
				streaming: true,
				reasoning: false,
				vision: false,
				tools: true,
				stability: "unstable",
				jsonOutput: true,
			},
		],
	},
	{
		id: "glm-4.5-flash",
		name: "GLM-4.5 Flash",
		description: "Free, fast GLM-4.5 model.",
		family: "zai",
		free: true,
		stability: "unstable",
		releasedAt: new Date("2025-08-13"),
		providers: [
			{
				providerId: "zai",
				externalId: "glm-4.5-flash",
				deactivatedAt: new Date("2026-06-28"),
				inputPrice: "0",
				cachedInputPrice: "0",
				outputPrice: "0",
				requestPrice: "0",
				contextSize: 128000,
				maxOutput: undefined,
				streaming: true,
				reasoning: false,
				vision: false,
				tools: true,
				jsonOutput: true,
			},
		],
	},
	{
		id: "glm-4.7",
		name: "GLM-4.7",
		description: "Latest GLM with enhanced reasoning capabilities.",
		family: "zai",
		releasedAt: new Date("2025-12-22"),
		providers: [
			{
				providerId: "zai",
				test: "skip",
				externalId: "glm-4.7",
				inputPrice: "0.6e-6",
				cachedInputPrice: "0.11e-6",
				outputPrice: "2.2e-6",
				requestPrice: "0",
				contextSize: 200000,
				maxOutput: 128000,
				streaming: true,
				reasoning: true,
				vision: false,
				tools: true,
				webSearch: true,
				webSearchPrice: "0.01",
				jsonOutput: true,
			},
			{
				providerId: "novita",
				test: "skip", // skip tests due to reasoning issues
				externalId: "zai-org/glm-4.7",
				inputPrice: "0.6e-6",
				cachedInputPrice: "0.11e-6",
				outputPrice: "2.2e-6",
				requestPrice: "0",
				contextSize: 204800,
				maxOutput: 128000,
				quantization: "fp8",
				streaming: true,
				reasoning: true,
				vision: false,
				tools: true,
				jsonOutput: true,
			},
			{
				providerId: "cerebras",
				test: "skip",
				externalId: "zai-glm-4.7",
				inputPrice: "2.25e-6",
				outputPrice: "2.75e-6",
				requestPrice: "0",
				contextSize: 200000,
				maxOutput: 128000,
				streaming: true,
				reasoning: true,
				vision: false,
				tools: true,
				jsonOutput: true,
				supportedParameters: [
					"temperature",
					"max_tokens",
					"top_p",
					"response_format",
					"tools",
					"tool_choice",
				],
			},
			{
				providerId: "bytedance",
				externalId: "glm-4-7-251222",
				inputPrice: "0.6e-6",
				cachedInputPrice: "0.11e-6",
				outputPrice: "2.2e-6",
				requestPrice: "0",
				contextSize: 200000,
				maxOutput: 128000,
				streaming: true,
				reasoning: true,
				vision: false,
				tools: true,
				jsonOutput: false, // Returns JSON wrapped in markdown code blocks
			},
			{
				providerId: "together-ai",
				test: "skip", // skip tests due to reasoning issues
				externalId: "zai-org/GLM-4.7",
				inputPrice: "0.45e-6",
				outputPrice: "2.0e-6",
				requestPrice: "0",
				contextSize: 202752,
				maxOutput: 128000,
				streaming: true,
				reasoning: true,
				vision: false,
				tools: false,
				jsonOutput: false,
			},
			{
				test: "skip",
				providerId: "alibaba",
				externalId: "glm-4.7",
				inputPrice: "0.431e-6",
				outputPrice: "2.007e-6",
				pricingTiers: [
					{
						name: "Up to 32K",
						upToTokens: 32000,
						inputPrice: "0.431e-6",
						outputPrice: "2.007e-6",
					},
					{
						name: "Over 32K",
						upToTokens: Infinity,
						inputPrice: "0.574e-6",
						outputPrice: "2.294e-6",
					},
				],
				regions: [{ id: "cn-beijing" }],
				requestPrice: "0",
				contextSize: 202752,
				maxOutput: 16384,
				streaming: true,
				reasoning: true,
				vision: false,
				tools: true,
				jsonOutput: true,
				deactivatedAt: new Date("2026-07-08"),
			},
			{
				providerId: "embercloud",
				externalId: "glm-4.7",
				// embercloud reasoning requests time out repeatedly in e2e
				stability: "unstable",
				inputPrice: "0.38e-6",
				outputPrice: "1.98e-6",
				cachedInputPrice: "0.19e-6",
				requestPrice: "0",
				contextSize: 200000,
				maxOutput: 131000,
				streaming: true,
				vision: false,
				tools: true,
				reasoning: true,
				jsonOutput: true,
				supportedParameters: [
					"messages",
					"model",
					"stream",
					"stream_options",
					"temperature",
					"top_p",
					"max_tokens",
					"max_completion_tokens",
					"seed",
					"stop",
					"response_format",
					"logprobs",
					"top_logprobs",
					"tools",
					"tool_choice",
					"parallel_tool_calls",
					"reasoning",
					"reasoning_effort",
				],
			},
			{
				providerId: "vertex-openai",
				externalId: "zai-org/glm-4.7-maas",
				deactivatedAt: new Date("2026-10-21"),
				inputPrice: "0.6e-6",
				outputPrice: "2.2e-6",
				requestPrice: "0",
				contextSize: 202752,
				maxOutput: 128000,
				streaming: true,
				reasoning: true,
				vision: false,
				tools: true,
				jsonOutput: true,
			},
		],
	},
	{
		id: "glm-4.7-flashx",
		name: "GLM-4.7 FlashX",
		description: "Lightweight, high-speed GLM-4.7 variant.",
		family: "zai",
		releasedAt: new Date("2025-12-22"),
		providers: [
			{
				providerId: "zai",
				externalId: "glm-4.7-flashx",
				// Frequently times out on multi-turn tool call requests
				stability: "unstable",
				inputPrice: "0.07e-6",
				cachedInputPrice: "0.01e-6",
				outputPrice: "0.4e-6",
				requestPrice: "0",
				contextSize: 200000,
				maxOutput: 128000,
				streaming: true,
				reasoning: true,
				vision: false,
				tools: true,
				jsonOutput: true,
			},
		],
	},
	{
		// Free GLM-4.7 Flash, served by zai at zero cost. Kept as a separate root
		// model from the paid embercloud mapping below so `free: true` strictly
		// implies every provider mapping is free (no free model carries a paid
		// provider). This is the model end-user test/sandbox wallets can use.
		id: "glm-4.7-flash-free",
		name: "GLM-4.7 Flash (Free)",
		description: "Free, lightweight GLM-4.7 model.",
		family: "zai",
		free: true,
		stability: "unstable",
		releasedAt: new Date("2025-12-22"),
		providers: [
			{
				providerId: "zai",
				externalId: "glm-4.7-flash",
				deactivatedAt: new Date("2026-06-28"),
				inputPrice: "0",
				cachedInputPrice: "0",
				outputPrice: "0",
				requestPrice: "0",
				contextSize: 200000,
				maxOutput: 128000,
				streaming: true,
				reasoning: true,
				vision: false,
				tools: true,
				jsonOutput: true,
			},
		],
	},
	{
		// Paid GLM-4.7 Flash, served by embercloud (PAYG). Not free.
		id: "glm-4.7-flash",
		name: "GLM-4.7 Flash",
		description: "Lightweight, high-speed GLM-4.7 model.",
		family: "zai",
		stability: "unstable",
		releasedAt: new Date("2025-12-22"),
		providers: [
			{
				providerId: "embercloud",
				externalId: "glm-4.7-flash",
				// embercloud rate-limits this model on every request (429 even for
				// single spaced-out calls, verified 2026-07-14)
				stability: "unstable",
				inputPrice: "0.06e-6",
				outputPrice: "0.4e-6",
				cachedInputPrice: "0.01e-6",
				requestPrice: "0",
				contextSize: 200000,
				maxOutput: 131000,
				streaming: true,
				vision: false,
				tools: true,
				jsonOutput: true,
				supportedParameters: [
					"messages",
					"model",
					"stream",
					"stream_options",
					"temperature",
					"top_p",
					"max_tokens",
					"max_completion_tokens",
					"seed",
					"stop",
					"response_format",
					"logprobs",
					"top_logprobs",
					"tools",
					"tool_choice",
					"parallel_tool_calls",
				],
			},
		],
	},
	{
		id: "glm-4.6",
		name: "GLM-4.6",
		description: "Updated GLM with reasoning capabilities.",
		family: "zai",
		releasedAt: new Date("2025-09-30"),
		providers: [
			{
				providerId: "zai",
				externalId: "glm-4.6",
				inputPrice: "0.6e-6",
				cachedInputPrice: "0.11e-6",
				outputPrice: "2.2e-6",
				requestPrice: "0",
				contextSize: 200000,
				maxOutput: undefined,
				streaming: true,
				reasoning: true,
				vision: false,
				tools: true,
				// zai hangs indefinitely on tool_choice "required" and named
				// function choices for glm-4.6 (verified live 2026-07-14)
				supportedToolChoices: ["auto", "none"],
				webSearch: true,
				webSearchPrice: "0.01",
				jsonOutput: true,
			},
			{
				providerId: "cerebras",
				test: "skip",
				deactivatedAt: new Date("2026-01-20"),
				externalId: "zai-glm-4.6",
				inputPrice: "2.25e-6",
				outputPrice: "2.75e-6",
				requestPrice: "0",
				contextSize: 200000,
				maxOutput: undefined,
				streaming: true,
				reasoning: true,
				vision: false,
				tools: true,
				jsonOutput: true,
			},
			{
				test: "skip",
				providerId: "alibaba",
				externalId: "glm-4.6",
				inputPrice: "0.431e-6",
				outputPrice: "2.007e-6",
				pricingTiers: [
					{
						name: "Up to 32K",
						upToTokens: 32000,
						inputPrice: "0.431e-6",
						outputPrice: "2.007e-6",
					},
					{
						name: "Over 32K",
						upToTokens: Infinity,
						inputPrice: "0.574e-6",
						outputPrice: "2.294e-6",
					},
				],
				regions: [{ id: "cn-beijing" }],
				requestPrice: "0",
				contextSize: 202752,
				maxOutput: 16384,
				streaming: true,
				reasoning: true,
				vision: false,
				tools: true,
				jsonOutput: true,
				deactivatedAt: new Date("2026-07-08"),
			},
			{
				providerId: "novita",
				test: "skip",
				externalId: "zai-org/glm-4.6",
				inputPrice: "0.55e-6",
				cachedInputPrice: "0.11e-6",
				outputPrice: "2.2e-6",
				requestPrice: "0",
				contextSize: 204800,
				maxOutput: undefined,
				quantization: "bf16",
				streaming: true,
				reasoning: true,
				vision: false,
				tools: true,
				jsonOutput: true,
			},
		],
	},
	{
		id: "glm-4-32b-0414-128k",
		name: "GLM-4 32B (0414-128k)",
		description: "GLM-4 32B instruction-tuned model.",
		family: "zai",
		releasedAt: new Date("2025-04-14"),
		providers: [
			{
				providerId: "zai",
				externalId: "glm-4-32b-0414-128k",
				inputPrice: "0.1e-6",
				cachedInputPrice: "0",
				outputPrice: "0.1e-6",
				requestPrice: "0",
				contextSize: 128000,
				maxOutput: undefined,
				streaming: true,
				reasoning: false,
				vision: false,
				tools: true,
				jsonOutput: true,
			},
		],
	},
	{
		id: "glm-4.6v",
		name: "GLM-4.6V",
		description:
			"Flagship vision-language model (106B) with native function calling support.",
		family: "zai",
		releasedAt: new Date("2025-12-08"),
		providers: [
			{
				providerId: "zai",
				externalId: "glm-4.6v",
				inputPrice: "0.3e-6",
				cachedInputPrice: "0.05e-6",
				outputPrice: "0.9e-6",
				requestPrice: "0",
				contextSize: 128000,
				maxOutput: 16000,
				streaming: true,
				reasoning: true,
				vision: true,
				tools: true,
				jsonOutput: true,
			},
			{
				providerId: "novita",
				externalId: "zai-org/glm-4.6v",
				inputPrice: "0.3e-6",
				cachedInputPrice: "0.055e-6",
				outputPrice: "0.9e-6",
				requestPrice: "0",
				contextSize: 131072,
				maxOutput: 16000,
				quantization: "bf16",
				streaming: true,
				reasoning: true,
				vision: true,
				tools: true,
				jsonOutput: true,
			},
		],
	},
	{
		id: "glm-4.6v-flashx",
		name: "GLM-4.6V FlashX",
		description: "Fast vision-language model with extended capabilities.",
		family: "zai",
		releasedAt: new Date("2025-12-08"),
		providers: [
			{
				providerId: "zai",
				externalId: "glm-4.6v-flashx",
				inputPrice: "0.04e-6",
				cachedInputPrice: "0.004e-6",
				outputPrice: "0.4e-6",
				requestPrice: "0",
				contextSize: 128000,
				maxOutput: 16000,
				streaming: true,
				reasoning: true,
				vision: true,
				tools: true,
				jsonOutput: true,
			},
		],
	},
	{
		id: "glm-4.6v-flash",
		name: "GLM-4.6V Flash",
		description:
			"Ultra-fast, lightweight vision-language model (9B) for low-latency workloads.",
		family: "zai",
		free: true,
		stability: "unstable",
		releasedAt: new Date("2025-12-08"),
		providers: [
			{
				providerId: "zai",
				externalId: "glm-4.6v-flash",
				deactivatedAt: new Date("2026-06-28"),
				inputPrice: "0",
				cachedInputPrice: "0",
				outputPrice: "0",
				requestPrice: "0",
				contextSize: 128000,
				maxOutput: 16000,
				streaming: true,
				reasoning: true,
				vision: true,
				tools: true,
				jsonOutput: true,
				supportedParameters: [
					"messages",
					"model",
					"stream",
					"stream_options",
					"temperature",
					"top_p",
					"max_tokens",
					"max_completion_tokens",
					"seed",
					"stop",
					"response_format",
					"tools",
					"tool_choice",
					"parallel_tool_calls",
					"reasoning",
					"reasoning_effort",
				],
			},
		],
	},
	{
		id: "cogview-4",
		name: "CogView-4",
		description:
			"Z.AI's CogView-4 text-to-image generation model with bilingual support and excellent text rendering.",
		family: "zai",
		output: ["text", "image"],
		releasedAt: new Date("2025-03-04"),
		providers: [
			{
				test: "skip",
				providerId: "zai",
				externalId: "cogView-4-250304",
				inputPrice: "0",
				outputPrice: "0",
				requestPrice: "0.01",
				contextSize: 2000,
				maxOutput: 4096,
				streaming: false,
				vision: false,
				tools: false,
				jsonOutput: false,
				imageGenerations: true,
			},
		],
	},
	{
		id: "glm-image",
		name: "GLM-Image",
		description:
			"Z.AI's GLM-Image text-to-image generation model with hybrid auto-regressive architecture, excellent for text-rendering and knowledge-intensive generation.",
		family: "zai",
		output: ["text", "image"],
		releasedAt: new Date("2025-01-14"),
		providers: [
			{
				test: "skip",
				providerId: "zai",
				externalId: "glm-image",
				inputPrice: "0",
				outputPrice: "0",
				requestPrice: "0.015",
				contextSize: 2000,
				maxOutput: 4096,
				streaming: false,
				vision: false,
				tools: false,
				jsonOutput: false,
				imageGenerations: true,
			},
		],
	},
] as const satisfies ModelDefinition[];
